import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@headlessui/react';
import { 
  FaPlus, FaLink, FaExternalLinkAlt, FaChartBar, FaGlobe, FaAndroid, FaApple, FaMousePointer, FaMobile, FaQuestionCircle
} from 'react-icons/fa';
import { 
  getToken, 
  getUser, 
  isTokenValid, 
  redirectToLogin,
  authenticatedFetch,
  getBaseUrl,
  getUserLimits
} from '../utils/tokenUtils';
import ShortLinkList from '../components/ShortLinkList';
import ShortLinkForm from '../components/ShortLinkForm';
// UpgradeModal handled by parent App.js
import StatsCard from '../components/StatsCard';
import VideoModal from '../components/VideoModal';

const ShortLinks = ({ user, onLogout, onStatsUpdate, onShowFeedbackModal, onUpgradeClick }) => {
  const navigate = useNavigate();
  const [shortLinks, setShortLinks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingShortLink, setEditingShortLink] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Modal states handled by parent App.js
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Check if user can create more short links
  const canCreateShortLink = () => {
    const currentCount = shortLinks.length;
    const limits = getUserLimits();
    
    if (!limits) {
      return false; // Limits henüz yüklenmemiş
    }
    
    const userPlan = limits.plan || 'free';
    const userLimit = limits.maxShortLinks;
    
    // Enterprise planında sınırsız
    if (userPlan === 'enterprise' || userLimit === -1) {
      return true;
    }
    
    // Kullanıcının limitini kullan, yoksa fallback
    if (userLimit !== undefined) {
      return currentCount < userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return currentCount < 1;
      case 'premium':
        return currentCount < 5;
      default:
        return currentCount < 1;
    }
  };

  const getShortLinkLimit = () => {
    const limits = getUserLimits();
    
    if (!limits) {
      return 1; // Default free plan limit
    }
    
    const userPlan = limits.plan || 'free';
    const userLimit = limits.maxShortLinks;
    
    // Enterprise planında sınırsız
    if (userPlan === 'enterprise' || userLimit === -1) {
      return 'Unlimited';
    }
    
    // Kullanıcının limitini kullan, yoksa fallback
    if (userLimit !== undefined) {
      return userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return 1;
      case 'premium':
        return 5;
      default:
        return 1;
    }
  };


  const handleUpgradeClick = (section = null) => {
    if (onUpgradeClick) {
      onUpgradeClick(section);
    }
  };

  // handleUpgradeSuccess handled by parent App.js


  useEffect(() => {
    fetchShortLinks();
    fetchProjects();
    fetchUserData();
  }, []);

  // Fetch fresh user data
  const fetchUserData = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch('/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          // Update localStorage with fresh user data
          localStorage.setItem('user', JSON.stringify(data.user));
          // Trigger parent component to update user state
          if (onStatsUpdate) {
            onStatsUpdate();
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Listen upgrade button from header
  useEffect(() => {
    const onHeaderUpgrade = (e) => {
      const section = e?.detail?.section || 'shortlinks';
      handleUpgradeClick(section);
    };
    window.addEventListener('open-upgrade-modal', onHeaderUpgrade);
    return () => window.removeEventListener('open-upgrade-modal', onHeaderUpgrade);
  }, []);

  // Toast notification functions
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };


  const fetchShortLinks = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch('/api/shortlink');
      const data = await response.json();
      setShortLinks(data.shortLinks || []);
    } catch (error) {
      console.error('Error fetching short links:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      }
    }
  };

  const fetchProjects = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch('/api/dashboard/user/projects?limit=0');
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error.message === 'Authentication failed') {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShortLink = async (shortLinkData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      console.log('Creating short link with data:', shortLinkData);

      const response = await authenticatedFetch('/api/shortlink', {
        method: 'POST',
        body: shortLinkData
      });

      if (response.status === 403) {
        const errorData = await response.json();
        // Check if it's a limit error
        if (errorData.message && errorData.message.includes('limitinize ulaştınız')) {
          handleUpgradeClick('shortlinks');
        } else {
          showToast(errorData.message, 'error');
        }
        return;
      }

      const result = await response.json();
      setShortLinks(prev => [...prev, result.shortLink]);
      setShowForm(false);
      setEditingShortLink(null);
      showToast('Short link created successfully!', 'success');
      
      // Refresh stats in parent component
      if (onStatsUpdate) {
        onStatsUpdate();
      }
    } catch (error) {
      console.error('Create short link error:', error);
      if (error.message !== 'Authentication failed') {
        // Check if it's a limit error
        if (error.message && error.message.includes('limitinize ulaştınız')) {
          handleUpgradeClick('shortlinks');
        } else {
          showToast(`Error occurred while creating short link: ${error.message}`, 'error');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateShortLink = async (shortLinkId, shortLinkData) => {
    setIsSubmitting(true);
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      // Only allow updating URLs and title (projectId and shortCode should not change)
      const { shortCode, isActive, projectId, ...updateData } = shortLinkData;
      
      const response = await authenticatedFetch(`/api/shortlink/${shortLinkId}`, {
        method: 'PUT',
        body: updateData
      });

      const result = await response.json();
      setShortLinks(prev => prev.map(sl => sl._id === shortLinkId ? result.shortLink : sl));
      setShowForm(false);
      setEditingShortLink(null);
      showToast('Short link updated successfully!', 'success');
    } catch (error) {
      console.error('Update short link error:', error);
      if (error.message !== 'Authentication failed') {
        showToast(`Error occurred while updating short link: ${error.message}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteShortLink = async (shortLink) => {
    if (!window.confirm('Are you sure you want to delete this short link? This action cannot be undone.')) {
      return;
    }

    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch(`/api/shortlink/${shortLink._id}`, {
        method: 'DELETE'
      });

      setShortLinks(prev => prev.filter(sl => sl._id !== shortLink._id));
      showToast('Short link deleted successfully!', 'success');
      
      // Refresh stats in parent component
      if (onStatsUpdate) {
        onStatsUpdate();
      }
    } catch (error) {
      console.error('Delete short link error:', error);
      if (error.message !== 'Authentication failed') {
        showToast('Error occurred while deleting short link.', 'error');
      }
    }
  };


  const filteredShortLinks = selectedProject 
    ? shortLinks.filter(link => link.projectId._id === selectedProject)
    : shortLinks;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 left-4 right-4 lg:left-auto lg:right-4 lg:max-w-sm z-[60] px-4 lg:px-6 py-3 rounded-lg shadow-lg transition-all duration-300 text-sm lg:text-base ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="p-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Links"
            value={shortLinks.length}
            icon={FaLink}
            showLimit={true}
            current={shortLinks.length}
            limit={(() => {
              const limits = getUserLimits();
              if (!limits) return 1;
              return limits.plan === 'enterprise' || limits.maxShortLinks === -1 ? -1 : (limits.maxShortLinks || 1);
            })()}
          />
          
          <StatsCard
            title="Total Clicks"
            value={shortLinks.reduce((total, link) => total + (link.analytics?.totalClicks || 0), 0)}
            icon={FaMousePointer}
          />
          
          <StatsCard
            title="Web Clicks"
            value={shortLinks.reduce((total, link) => total + (link.analytics?.platforms?.web || 0) + (link.analytics?.platforms?.desktop || 0), 0)}
            icon={FaGlobe}
          />
          
          <StatsCard
            title="Mobile Clicks"
            value={shortLinks.reduce((total, link) => total + (link.analytics?.platforms?.android || 0) + (link.analytics?.platforms?.ios || 0), 0)}
            icon={FaMobile}
          />
        </div>

        {/* Short Links Section */}
        <div className="bg-black rounded-lg border border-gray-800">
          <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
            <h1 className="text-lg font-medium text-white">Short Links</h1>
            <div className="flex space-x-3">
              <Button
                onClick={() => setShowVideoModal(true)}
                className="group flex items-center px-3 lg:px-4 py-2 lg:py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white text-sm lg:text-base"
              >
                <FaQuestionCircle className="mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 group-hover:scale-110" />
                <span className="hidden lg:inline">How to Create?</span>
                <span className="lg:hidden">How to?</span>
              </Button>
              <Button
                onClick={() => {
                  if (!canCreateShortLink()) {
                    handleUpgradeClick('shortlinks');
                    return;
                  }
                  setEditingShortLink(null);
                  setShowForm(true);
                }}
                className={`group flex items-center px-4 lg:px-6 py-2 lg:py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border text-sm lg:text-base ${
                  canCreateShortLink() 
                    ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
                }`}
              >
                <FaPlus className={`mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 ${
                  canCreateShortLink() ? 'group-hover:rotate-90' : 'animate-bounce'
                }`} />
                <span className="hidden lg:inline">
                  {canCreateShortLink() ? 'New Short Link' : 'Upgrade to Create More'}
                </span>
                <span className="lg:hidden">
                  {canCreateShortLink() ? 'New Link' : 'Upgrade'}
                </span>
              </Button>
            </div>
          </div>

          {/* Project Filter */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <label className="text-sm font-medium text-gray-300">
                Project Filter:
              </label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filteredShortLinks.length === 0 ? (
            <div className="text-center py-8 lg:py-12">
              <FaLink className="mx-auto h-10 w-10 lg:h-12 lg:w-12 text-gray-400 mb-3 lg:mb-4" />
              <h3 className="text-base lg:text-lg font-medium text-white mb-2">No short links yet</h3>
              <p className="text-gray-400 mb-4 lg:mb-6 text-sm lg:text-base">Get started by creating your first short link</p>
              <div className="flex justify-center space-x-3">
                <Button
                  onClick={() => setShowVideoModal(true)}
                  className="group flex items-center px-3 lg:px-4 py-2 lg:py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white text-sm lg:text-base"
                >
                  <FaQuestionCircle className="mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 group-hover:scale-110" />
                  <span className="hidden lg:inline">How to Create?</span>
                  <span className="lg:hidden">How to?</span>
                </Button>
                <Button
                  onClick={() => {
                    if (!canCreateShortLink()) {
                      handleUpgradeClick('shortlinks');
                      return;
                    }
                    setEditingShortLink(null);
                    setShowForm(true);
                  }}
                  className={`group flex items-center px-4 lg:px-6 py-2 lg:py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border text-sm lg:text-base ${
                    canCreateShortLink() 
                      ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
                  }`}
                >
                  <FaPlus className={`mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 ${
                    canCreateShortLink() ? 'group-hover:rotate-90' : 'animate-bounce'
                  }`} />
                  <span className="hidden lg:inline">
                    {canCreateShortLink() ? 'New Short Link' : 'Upgrade to Create More'}
                  </span>
                  <span className="lg:hidden">
                    {canCreateShortLink() ? 'New Link' : 'Upgrade'}
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <ShortLinkList
              shortLinks={filteredShortLinks}
              projects={projects}
              onEdit={(shortLink) => {
                setEditingShortLink(shortLink);
                setShowForm(true);
              }}
              onDelete={handleDeleteShortLink}
              onRefresh={fetchShortLinks}
              onShowToast={showToast}
            />
          )}
          </div>
        </div>
      </div>

      {/* Short Link Form Modal */}
      {showForm && (
        <ShortLinkForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingShortLink(null);
          }}
          onSubmit={editingShortLink ? (data) => handleUpdateShortLink(editingShortLink._id, data) : handleCreateShortLink}
          shortLink={editingShortLink}
          projects={projects}
          isLoading={isSubmitting}
        />
      )}


      {/* Upgrade Modal handled by parent App.js */}

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl="https://www.youtube.com/embed/NSBXNag5Ulg"
        title="How to Create a Short Link?"
      />


    </div>
  );
};

export default ShortLinks;